import { useEffect, useRef, useState } from "react";
import { isMobile } from 'react-device-detect';
import { useNavigate } from "react-router-dom";
import { GUEST_ROUTES } from 'routes/routes';
import styled from "styled-components";
import { ICharacter } from 'types/characters';

interface ICharacterProps {
  character: ICharacter;
}

const CharacterCard: React.FC<ICharacterProps> = (props) => {
  const { character } = props;
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  const handleCardClick = () => navigate(GUEST_ROUTES.CHARACTER(character.id));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (isMobile) {
            setIsVisible(entry.isIntersecting);
            setShouldLoadVideo(true);
          }
          if (entry.isIntersecting && !isMobile) {
            setShouldLoadVideo(true);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      if (isVisible) {
        videoRef.current.play().catch(() => { });
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isVisible]);

  return (
    <Card
      ref={containerRef}
      onClick={handleCardClick}
      onMouseEnter={() => {
        if (character?.videoCoverUrl && !isMobile) {
          setIsVisible(true);
        }
      }}
      onMouseLeave={() => {
        if (character?.videoCoverUrl && !isMobile) {
          setIsVisible(false);
        }
      }}
    >
      <Cover src={character.coverURL ?? character.avatar} alt={character.name} />
      {character?.videoCoverUrl && shouldLoadVideo && (
        <CardVideo
          visible={isVisible}
          ref={videoRef}
          muted
          playsInline
          preload={isMobile ? "none" : "auto"}
          onEnded={() => setIsVisible(false)}
        >
          <source src={character.videoCoverUrl} type="video/mp4" />
        </CardVideo>
      )}
      <BottomSection>
        <Name>{character.name}</Name>
        <Description>{character.description}</Description>
      </BottomSection>
    </Card>
  );
};

export default CharacterCard;

const Card = styled.div`
  width: 308px;
  height: 396px;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    width: 100%;
    height: 440px;
  }
`;

const Cover = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const BottomSection = styled.div`
  position: absolute;
  bottom: -1px;
  width: 100%;
  padding: 32px 16px 24px 16px;
  background: linear-gradient(180deg, rgba(29, 29, 29, 0.00) 0%, rgba(29, 29, 29, 0.70) 32.89%, #100D0B 100%);
  color: #fff;
  z-index: 3;
`;

const Name = styled.span`
  font-family: Raleway, sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
`;

const Description = styled.p`
  margin-top: 4px;
  font-family: Nunito Sans;
  font-weight: 400;
  font-size: 16px;
  line-height: 18px;
  text-overflow: ellipsis;
  overflow: hidden;
  color: #A9A9A9;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;
const CardVideo = styled.video<{ visible: boolean }>`
  z-index: 2;
  object-fit: cover;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  opacity: ${props => (props?.visible ? '1' : '0')};
  transition: opacity 0.3s ease;
`;

